import { useCallback } from 'react'
import { useReactiveVar } from '@apollo/client'
import { Box, Button } from '@mui/material'
import { openDecisionPage } from 'utils/navigationHelper'
import DescriptionDisabledIcon from 'assets/icons/description-disabled-icon.svg'
import DescriptionIcon from 'assets/icons/description-icon.svg'
import FolderDisabledIcon from 'assets/icons/folder-disabled-icon.svg'
import FolderIcon from 'assets/icons/folder-icon.svg'
import GroupIcon from 'assets/icons/group-icon.svg'
import RefreshIcon from 'assets/icons/refresh-icon.svg'
import { clearAlertQueue, setAlertQueue } from 'cache/alerts'
import { selectedApprovalRowsVar } from 'cache/approvals'
import errorMessages from 'constants/errorMessages'
import { MessageType } from 'ts/enums/messageType.enums'
import styles from '../styles.module.scss'

const MANAGE_BACKUP_URL = process.env.REACT_APP_BACKUP_URL ?? '/manage-backup'
const PRICEHUB_URL = process.env.REACT_APP_PRICEHUB_URL

type ApprovalsToolbarProps = {
  onRefresh: () => void
  lastRefreshLabel?: string | null
  loading?: boolean
}

const ApprovalsToolbar = ({ onRefresh, lastRefreshLabel, loading }: ApprovalsToolbarProps) => {
  const selectedRows = useReactiveVar(selectedApprovalRowsVar)
  const hasSelectedRows = selectedRows?.length > 0
  const selectedDeal = hasSelectedRows ? selectedRows[0] : null
  const hasDecisionPagePath = Boolean(selectedDeal?.decisionPagePath?.trim())

  const handleManageBackupClick = useCallback(() => {
    const newWindow = window.open(MANAGE_BACKUP_URL, '_blank', 'noopener,noreferrer')
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      console.error('Failed to open Manage Backup. Please check popup blocker settings.')
    }
  }, [])

  const handleDecide = useCallback(() => {
    if (selectedDeal && hasDecisionPagePath) {
      openDecisionPage(selectedDeal)
    }
  }, [hasDecisionPagePath, selectedDeal])

  const handleOpenPriceHub = useCallback(() => {
    if (selectedRows && selectedRows.length > 0) {
      const { dealNr } = selectedRows[0]
      setAlertQueue({
        type: MessageType.INFO,
        message: errorMessages.VPN_CONNECTION_WARNING,
        onClose: () => {
          sessionStorage.clear()
          clearAlertQueue()
        },
        onOk: () => {
          window.open(`${PRICEHUB_URL}/deal/${dealNr}`, '_blank', 'noopener,noreferrer')
        },
      })
    }
  }, [selectedRows])

  return (
    <Box className={styles.toolbarRoot}>
      <Box className={styles.leftActions}>
        <Button
          className={styles.toolbarButton}
          onClick={handleDecide}
          startIcon={
            <img
              src={hasDecisionPagePath ? DescriptionIcon : DescriptionDisabledIcon}
              alt="Decide Icon"
            />
          }
          disabled={!hasDecisionPagePath}
          aria-label="Decide on selected approval deal"
          title="Review and make a decision on the selected approval"
        >
          Decide
        </Button>
        <Button
          className={styles.toolbarButton}
          startIcon={
            <img
              src={hasSelectedRows ? FolderIcon : FolderDisabledIcon}
              alt="Open in PriceHub Icon"
            />
          }
          disabled={!hasSelectedRows}
          aria-label="Open selected approval deal in PriceHub"
          title="Open the selected approval deal in PriceHub"
          onClick={handleOpenPriceHub}
        >
          Open in PriceHub
        </Button>
      </Box>
      <Box className={styles.rightActions}>
        <Box className={styles.refreshButtonContainer}>
          {lastRefreshLabel && (
            <span className={styles.refreshStatus} aria-label="approvals-last-refresh-label">
              {lastRefreshLabel}
            </span>
          )}
          <Button
            className={styles.toolbarButton}
            startIcon={<img src={RefreshIcon} alt="Refresh Icon" />}
            aria-label="Refresh approvals list"
            title="Refresh the list of approvals"
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
        <Button
          className={styles.toolbarButton}
          startIcon={<img src={GroupIcon} alt="Manage Backup Icon" />}
          onClick={handleManageBackupClick}
          aria-label="Manage Backup"
          title="Manage your backup settings"
          disabled={loading}
        >
          Manage Backup
        </Button>
      </Box>
    </Box>
  )
}

export default ApprovalsToolbar
